import React, { useEffect, useMemo } from 'react';
import { Alert, Modal, Spinner } from 'react-bootstrap';
import { useLazyGetExpenseItemsByExpenseItemIdQuery } from '../../../services/ExpenseApi';
import { useLazyGetInvoiceDetailByIdQuery } from '../../../services/InvoiceDataDraftApi';
import CurrencyComp from '../../../components/currencyComponent/CurrencyComp';
import DateTimeComp from '../../../components/dateComponent/DateTimeComp';

type Props = {
    show: boolean;
    handleClose: () => void;
    /** Row from All Receipts list (may be partial) */
    receiptRow?: any;
};

type CompareRow = {
    label: string;
    ocrValue: React.ReactNode;
    lineItemValue: React.ReactNode;
    mismatch?: boolean;
};

const norm = (v: any): string => {
    if (v === null || v === undefined || v === '') return '';
    if (typeof v === 'number') return String(v);
    return String(v).trim().toLowerCase();
};

const display = (v: any): React.ReactNode => {
    if (v === null || v === undefined || v === '') return <span className="text-muted">N/A</span>;
    return v;
};

const formatMoney = (v: any) =>
    v === null || v === undefined || v === '' ? null : <CurrencyComp amount={String(v)} />;

const formatDate = (v: any) =>
    v ? <DateTimeComp formattedDate={v} /> : null;

const CompareOcrExpenseItemModal = ({ show, handleClose, receiptRow }: Props) => {
    const lineItemId = Number(
        receiptRow?.expenseItemId || receiptRow?.usedExpenseItemId || 0
    );
    const draftId = Number(receiptRow?.invoiceDataDraftId || 0);

    const [triggerItem, { data: itemResp, isFetching: itemLoading, isError: itemError }] =
        useLazyGetExpenseItemsByExpenseItemIdQuery();
    const [triggerDraft, { data: draftResp, isFetching: draftLoading }] =
        useLazyGetInvoiceDetailByIdQuery();

    useEffect(() => {
        if (!show) return;
        if (lineItemId > 0) triggerItem({ expenseItemId: lineItemId });
        if (draftId > 0) triggerDraft(draftId);
    }, [show, lineItemId, draftId, triggerItem, triggerDraft]);

    const ocr = draftResp?.result || receiptRow || {};
    const item = itemResp?.result || null;

    const rows: CompareRow[] = useMemo(() => {
        const ocrAmount = ocr?.amount;
        const itemAmount = item?.amount;
        const ocrDate = ocr?.invoiceDate;
        const itemDate = item?.itemDate;
        const ocrVendor = ocr?.vendorName || ocr?.itemVendorName;
        const itemVendor = item?.vendor?.name || item?.vendorName || item?.merchantName;
        const ocrInvoiceNo = ocr?.invoiceNo;
        const itemInvoiceNo = item?.invoiceNo || item?.receiptNo;
        const ocrCard = ocr?.cardNo;
        const itemCard = item?.cardNo || item?.creditCardNo;

        const pairs: CompareRow[] = [
            {
                label: 'Vendor',
                ocrValue: display(ocrVendor),
                lineItemValue: display(itemVendor),
                mismatch: norm(ocrVendor) !== norm(itemVendor),
            },
            {
                label: 'Invoice / Receipt No',
                ocrValue: display(ocrInvoiceNo),
                lineItemValue: display(itemInvoiceNo),
                mismatch: norm(ocrInvoiceNo) !== norm(itemInvoiceNo),
            },
            {
                label: 'Date',
                ocrValue: display(formatDate(ocrDate) ?? ocrDate),
                lineItemValue: display(formatDate(itemDate) ?? itemDate),
                mismatch: norm(ocrDate)?.slice(0, 10) !== norm(itemDate)?.slice(0, 10),
            },
            {
                label: 'Amount',
                ocrValue: display(formatMoney(ocrAmount) ?? ocrAmount),
                lineItemValue: display(formatMoney(itemAmount) ?? itemAmount),
                mismatch: norm(ocrAmount) !== norm(itemAmount),
            },
            {
                label: 'Card Number',
                ocrValue: display(ocrCard),
                lineItemValue: display(itemCard),
                mismatch: norm(ocrCard) !== norm(itemCard),
            },
            {
                label: 'Category',
                ocrValue: display(ocr?.category),
                lineItemValue: display(
                    item?.expenseCategory?.title || item?.category?.title || item?.categoryName
                ),
            },
            {
                label: 'Payment Method',
                ocrValue: display(ocr?.expensePayment?.title),
                lineItemValue: display(
                    item?.paymentMethod?.title || item?.expensePaymentMethod?.title
                ),
            },
            {
                label: 'Expense Item ID',
                ocrValue: display(ocr?.expenseItemId || ocr?.usedExpenseItemId),
                lineItemValue: display(item?.expenseItemId),
            },
            {
                label: 'Tracking ID',
                ocrValue: display(ocr?.trackingId),
                lineItemValue: display(item?.trackingId || item?.expense?.trackingId),
            },
        ];
        return pairs;
    }, [ocr, item]);

    const isLoading = itemLoading || draftLoading;

    return (
        <Modal
            show={show}
            onHide={handleClose}
            centered
            scrollable
            dialogClassName="modal-compare-ocr-item"
        >
            <style>{`
                .modal-compare-ocr-item {
                    max-width: 75vw !important;
                    width: 75% !important;
                }
            `}</style>
            <Modal.Header closeButton>
                <Modal.Title className="fs-4">Expense Item vs Compare OCR</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Alert variant="info" className="mb-4">
                    <div className="fw-bold mb-2">How to read this comparison</div>
                    <ul className="mb-0 ps-4">
                        <li>
                            <strong>OCR Read (from attachment)</strong> — values extracted from the
                            receipt file and stored on Invoice Data Draft
                            {draftId ? ` (#${draftId})` : ''}.
                        </li>
                        <li>
                            <strong>Actual Line Item details</strong> — values currently saved on the
                            expense line item
                            {lineItemId ? ` (#${lineItemId})` : ''} in SpendSail.
                        </li>
                        <li>
                            Highlighted rows indicate a possible mismatch between OCR and the line
                            item.
                        </li>
                    </ul>
                </Alert>

                {!lineItemId ? (
                    <Alert variant="warning">
                        This receipt is not linked to an expense line item, so comparison is not
                        available.
                    </Alert>
                ) : isLoading ? (
                    <div className="text-center py-10">
                        <Spinner animation="border" variant="primary" />
                        <p className="text-muted mt-3 mb-0">Loading OCR and line item details…</p>
                    </div>
                ) : itemError || !item ? (
                    <Alert variant="danger">Unable to load expense line item details.</Alert>
                ) : (
                    <div className="table-responsive compare-ocr-table-wrap" style={{ maxHeight: '55vh' }}>
                        <table className="table table-row-bordered align-middle gs-0 gy-3 compare-ocr-table">
                            <thead className="sticky-top compare-ocr-thead">
                                <tr className="fw-bold text-muted">
                                    <th className="min-w-140px">Field</th>
                                    <th className="min-w-200px">
                                        OCR Read
                                        <div className="fs-8 fw-normal text-muted">From attachment</div>
                                    </th>
                                    <th className="min-w-200px">
                                        Actual Line Item
                                        <div className="fs-8 fw-normal text-muted">Expense item details</div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => (
                                    <tr
                                        key={row.label}
                                        className={row.mismatch ? 'compare-ocr-mismatch-row' : undefined}
                                    >
                                        <td className="fw-semibold compare-ocr-field-label">{row.label}</td>
                                        <td>{row.ocrValue}</td>
                                        <td>{row.lineItemValue}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn btn-light" onClick={handleClose}>
                    Close
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default CompareOcrExpenseItemModal;
